/**
 * Class strings the course player's parts share (plan docs/plans/lms-plan.md
 * section 13, G13; the design skill .claude/skills/siteoperations-design/SKILL.md).
 * The generic buttons come from frontend/src/ui/styles (imported by file path);
 * these are the player's own compositions over the same tokens: 44 px targets
 * on mobile collapsing from sm:, primary blue for chrome only, semantic green,
 * amber and red always paired with a word, dark: classes on everything.
 */

/** The player's content column: edge to edge on the page, a readable measure inside (G13). */
export const stageCls = 'mx-auto w-full max-w-5xl px-4 py-4 sm:px-6 lg:px-8 lg:py-6';

/** The reading document's measure. */
export const readingCls = 'mx-auto w-full max-w-3xl px-4 py-4 sm:px-6 lg:px-8 lg:py-6';

/** A panel (the design skill's card recipe). */
export const panelCls = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6';

/** A micro-label (text-[10px] uppercase tracking-widest). */
export const microLabelCls = 'text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400';

/** A slide or question heading: text-lg, never larger (13: no text-2xl titles). */
export const headingCls = 'text-lg font-semibold leading-7 text-gray-900 dark:text-gray-100';

/** Plain body text inside the player (slides use text-base for reading at a distance). */
export const bodyCls = 'text-base leading-relaxed text-gray-700 dark:text-gray-300';

/**
 * An answer option (a knowledge check, a scenario, a quiz question): a whole
 * row that is a 44 px touch target, with the selected and revealed states.
 */
export function optionCls(state: 'idle' | 'selected' | 'correct' | 'incorrect' | 'muted'): string {
  const base =
    'flex min-h-[44px] w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors duration-150 focus-within:ring-2 focus-within:ring-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500';
  switch (state) {
    case 'selected':
      return `${base} border-primary-600 bg-primary-50 text-gray-900 dark:border-primary-500 dark:bg-primary-900/20 dark:text-gray-100`;
    case 'correct':
      return `${base} border-green-200 bg-green-50 text-gray-900 dark:border-green-800 dark:bg-green-900/20 dark:text-gray-100`;
    case 'incorrect':
      return `${base} border-red-200 bg-red-50 text-gray-900 dark:border-red-800 dark:bg-red-900/20 dark:text-gray-100`;
    case 'muted':
      return `${base} border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300`;
    case 'idle':
    default:
      return `${base} cursor-pointer border-gray-300 bg-white text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700`;
  }
}

/** A status pill with its word (13: color never carries meaning alone). */
export function pillCls(tone: 'green' | 'amber' | 'red' | 'gray' | 'primary'): string {
  const base = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium';
  switch (tone) {
    case 'green':
      return `${base} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300`;
    case 'amber':
      return `${base} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`;
    case 'red':
      return `${base} bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300`;
    case 'primary':
      return `${base} bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300`;
    case 'gray':
    default:
      return `${base} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`;
  }
}

/** A notice line (semantic family per tone). */
export function noticeCls(tone: 'green' | 'amber' | 'red' | 'primary'): string {
  const base = 'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm';
  switch (tone) {
    case 'green':
      return `${base} border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400`;
    case 'amber':
      return `${base} border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300`;
    case 'red':
      return `${base} border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400`;
    case 'primary':
    default:
      return `${base} border-primary-200 bg-primary-50 text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300`;
  }
}
